import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { PayoutDetailsEntity } from "@modules/financesPayout/entities/payoutEntity"
import { PayoutEnumApi } from "@modules/financesPayout/gateway/payoutApi"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { downloadFile } from "@modules/utils/downloadFile"
import { LoggerType } from "@modules/utils/loggerUtils"
import * as Sentry from "@sentry/react"
import dayjs from "dayjs"
import { Download } from "lucide-react"
import { useCallback, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type PayoutDetailsDownloadModalRef = {
  open: () => void
}

type Props = {
  payoutDetails: PayoutDetailsEntity
  payoutEnumApi: PayoutEnumApi
  ref: React.Ref<PayoutDetailsDownloadModalRef>
}

export function PayoutDetailsDownloadModal({ payoutDetails, payoutEnumApi, ref }: Props) {
  const theme = useTheme()
  const [showModal, setShowModal] = useState(false)
  const [isLoadingCsv, setIsLoadingCsv] = useState(false)
  // const [isLoadingPdf, setIsLoadingPdf] = useState(false)
  const { userFinanceApi, boxFinanceApi } = useServicesContext()
  const financeApi = payoutEnumApi === PayoutEnumApi.OWNER ? boxFinanceApi : userFinanceApi
  const toast = useToast()

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const downloadCsv = async () => {
    try {
      setIsLoadingCsv(true)
      const transactions = payoutDetails.transactions
      const startDate = transactions[0]?.createdAt
      const endDate = transactions[transactions.length - 1]?.createdAt
      const result = await financeApi.exportTransactions(
        dayjs(startDate).startOf("day").toISOString(),
        dayjs(endDate).endOf("day").toISOString(),
      )
      downloadFile("transaction.csv", result, "text/csv")
      toast.show({
        severity: "success",
        summary: word("finances.page.export.success"),
      })
      onHide()
    } catch (e: any) {
      Sentry.captureException(`${LoggerType.Financial} Export csv data in payout ${e.message}`)
      toast.show({ severity: "error", summary: "Error", detail: e.message })
    } finally {
      setIsLoadingCsv(false)
    }
  }

  return (
    <HUDialog visible={showModal} header={null} onHide={onHide} style={{ width: "510px" }}>
      <HUText
        fontStyle="TL"
        color={theme.blackAtheleWhiteOwner}
        className="flex pt-4 mt-4 justify-content-center align-content-center text-center"
      >
        {word("global.download")}
      </HUText>
      <div className="flex flex-column mt-6 gap-2">
        <HUText fontStyle="BM" color={theme.neutralColor600}>
          {word("finances.payoutDownloadModal.csvText")}
        </HUText>
        <HUButton
          text={
            isLoadingCsv
              ? word("finances.payoutDownloadModal.downloadInProgress")
              : word("finances.payoutDownloadModal.csvDownload")
          }
          onClick={() => downloadCsv()}
          icon={{
            iconView: <Download size={15} color={theme.blackAtheleWhiteOwner} />,
          }}
          type="Right_Icon"
          size="M"
          colorType="Primary"
          disabled={isLoadingCsv}
        />
      </div>
      {/* <div className="flex flex-column mt-4 gap-2">
          <HUText fontStyle="BM" color={theme.neutralColor600}>
            {word("finances.payoutDownloadModal.pdfText")}
          </HUText>
          <HUButton
            text={isLoadingPdf ? word("finances.payoutDownloadModal.downloadInProgress") : word("finances.payoutDownloadModal.pdfDownload")}
            onClick={() => {}}
            icon={{
              iconView: <Download size={15} color={theme.blackAtheleWhiteOwner} />,
            }}
            type="Right_Icon"
            size="M"
            colorType="Primary"
            disabled={isLoadingPdf}
          />
        </div> */}
    </HUDialog>
  )
}
